<template>
  <Transition name="popup">
    <div
      v-show="isDisplayed"
      ref="pickerEl"
      class="recurrence-picker"
      :style="computedStyle"
      tabindex="0"
      @keydown.enter.stop="handleCreate"
      @keydown.escape.stop="closePicker"
    >
      <div class="recurrence-input">
        <input
          ref="inputEl"
          type="text"
          placeholder="day, workday, month until 2032..."
          class="natural-date-input"
          @keydown="handleRecurrenceInput"
        />
      </div>

      <!-- "Every" section -->
      <div class="every-section">
        <div class="section-title">Every</div>
        <div class="every-controls">
          <select v-model.number="form.every" class="number-select">
            <option v-for="n in 31" :key="n" :value="n">{{ n }}</option>
          </select>
          <select v-model="form.frequency" class="frequency-select">
            <option value="day">Day</option>
            <option value="week">Week</option>
            <option value="month">Month</option>
            <option value="year">Year</option>
          </select>
        </div>
      </div>

      <!-- "On" section for week frequency -->
      <div v-if="form.frequency === 'week'" class="on-section">
        <div class="section-title">On</div>
        <div class="days-checkboxes">
          <button
            v-for="day in weekDays"
            :key="day.short"
            class="day-button"
            :class="{ selected: form.days.includes(day.value) }"
            @click="selectDay(day.value)"
          >
            {{ day.short }}
          </button>
        </div>
      </div>

      <!-- "On" section for month frequency -->
      <div v-if="form.frequency === 'month'" class="on-section">
        <div class="section-title">On</div>
        <select v-model.number="form.dayOfMonth" class="day-select">
          <option v-for="day in monthDays" :key="day.value" :value="day.value">
            {{ day.label }}
          </option>
        </select>
      </div>

      <!-- "On" section for year frequency -->
      <div v-if="form.frequency === 'year'" class="on-section">
        <div class="section-title">On</div>
        <button
          class="date-button"
          @click="handleDateButtonClick($event, 'dateOfYear')"
        >
          {{ form.dateOfYear.formatted }}
        </button>
      </div>

      <!-- "Start" section -->
      <div class="starts-section">
        <div class="section-title">Start</div>
        <div class="starts-options">
          <label class="radio-option">
            <input
              v-model="form.startsType"
              type="radio"
              value="now"
              class="radio-input"
            />
            <span class="radio-label">Now</span>
          </label>
          <label class="radio-option">
            <input
              v-model="form.startsType"
              type="radio"
              value="from"
              class="radio-input"
            />
            <span class="radio-label">On</span>
          </label>
        </div>
        <button
          v-if="form.startsType === 'from'"
          class="date-button"
          @click="handleDateButtonClick($event, 'startDate')"
        >
          {{ form.startDate.formatted }}
        </button>
      </div>

      <!-- "End" section -->
      <div class="ends-section">
        <div class="section-title">End</div>
        <div class="ends-options">
          <label class="radio-option">
            <input
              v-model="form.endsType"
              type="radio"
              value="forever"
              class="radio-input"
            />
            <span class="radio-label">Forever</span>
          </label>
          <label class="radio-option">
            <input
              v-model="form.endsType"
              type="radio"
              value="until"
              class="radio-input"
            />
            <span class="radio-label">Until</span>
          </label>
        </div>
        <button
          v-if="form.endsType === 'until'"
          class="date-button"
          @click="handleDateButtonClick($event, 'endDate')"
        >
          {{ form.endDate.formatted }}
        </button>
      </div>

      <div class="actions">
        <BaseButton
          kind="auxiliary"
          text="Cancel"
          :tooltip="{
            text: 'Cancel',
            shortcut: modalActions.closeModal.shortcutsDisplay,
          }"
          tooltip-display="always"
          class="cancel-button"
          @click="closePicker"
        />
        <BaseButton
          kind="main"
          text="Create"
          :tooltip="{
            text: 'Create',
            shortcut: modalActions.validateModal.shortcutsDisplay,
          }"
          tooltip-display="always"
          class="create-button"
          @click="handleCreate"
        />
      </div>
    </div>
  </Transition>

  <!-- DatePicker for yearly recurring/starting date and end date -->
  <DatePicker ref="datePicker" :modes="['date']" :show-clear="false" />
</template>

<script lang="ts">
import {
  addYears,
  format,
  getDay,
  getDaysInMonth,
  isBefore,
  startOfDay,
} from "date-fns";
import {
  computed,
  defineComponent,
  nextTick,
  onBeforeUnmount,
  onMounted,
  Ref,
  ref,
  Transition,
  watch,
} from "vue";

import {
  MandateParserWrapper,
  MonthDay,
  Recurrence,
} from "@ctrl/controller-rpc";

import { modalActions } from "@/components/actions/actions";
import BaseButton from "@/components/base/BaseButton.vue";
import DatePicker from "@/components/DatePicker.vue";
import * as inject from "@/components/inject";
import { UseSettings } from "@/components/settings/controller/settings";
import { useOverlayPresence } from "@/composables/useOverlayPresence";
import { monthFromDate, monthIndex } from "@/utils/dates";

import { makeParser } from "./parsers";

const FREQUENCIES = ["day", "week", "month", "year"] as const;
type Frequency = (typeof FREQUENCIES)[number];
type EndsType = "forever" | "until";
type StartsType = "now" | "from";

const DEFAULT_EVERY = 1;
const DEFAULT_FREQUENCY: Frequency = "week";
const DEFAULT_STARTS_TYPE: StartsType = "now";
const DEFAULT_ENDS_TYPE: EndsType = "forever";

type Form = {
  every: number;
  frequency: Frequency;
  startsType: StartsType;
  endsType: EndsType;
  days: string[];
  dayOfMonth: MonthDay;
  dateOfYear: {
    value: Date;
    formatted: string;
  };
  startDate: {
    value: Date;
    formatted: string;
  };
  endDate: {
    value: Date;
    formatted: string;
  };
};

export default defineComponent({
  name: "RecurrencePicker",
  components: {
    BaseButton,
    DatePicker,
    Transition,
  },
  async setup() {
    const isDisplayed = ref(false);
    const releaseOverlayPresence = useOverlayPresence(isDisplayed);
    onBeforeUnmount(releaseOverlayPresence);
    const pickerEl = ref<HTMLElement | null>(null);
    const inputEl = ref<HTMLInputElement | null>(null);
    const computedStyle = ref({});
    const controller = inject.controller();
    const isInputFocused = inject.isInputFocused();
    const currentPosition = ref("left");
    const triggerEl = ref<HTMLElement | undefined>(undefined);
    const onCreationCallback = ref<((selection: Recurrence) => void) | null>(
      null
    );
    const parser = ref<MandateParserWrapper>();
    const settings = UseSettings.getInstance();

    // DatePicker state
    const datePicker = ref<InstanceType<typeof DatePicker> | null>(null);
    const datePickerTriggerEl = ref<HTMLElement | null>(null);

    onMounted(() => {
      window.addEventListener("resize", updatePosition);
      window.addEventListener("scroll", updatePosition);
      if (pickerEl.value) {
        pickerEl.value.addEventListener("transitionend", onTransitionEnd);
      }
    });

    onBeforeUnmount(() => {
      document.removeEventListener("mousedown", handleClickOutside, {
        capture: true,
      });
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition);
      if (pickerEl.value) {
        pickerEl.value.removeEventListener("transitionend", onTransitionEnd);
      }
    });
    const today = inject.today();
    const DAYS = [
      { short: "Sun", long: "Sunday", value: "sunday" },
      { short: "Mon", long: "Monday", value: "monday" },
      { short: "Tue", long: "Tuesday", value: "tuesday" },
      { short: "Wed", long: "Wednesday", value: "wednesday" },
      { short: "Thu", long: "Thursday", value: "thursday" },
      { short: "Fri", long: "Friday", value: "friday" },
      { short: "Sat", long: "Saturday", value: "saturday" },
    ];

    const currentDate = computed(() => {
      const dayIndex = getDay(today.value);
      return {
        name: {
          short: DAYS[dayIndex].short,
          long: DAYS[dayIndex].long,
        },
        value: DAYS[dayIndex].value,
        day: {
          formatted: format(today.value, "MMMM do"),
        },
        month: {
          number: today.value.getDate(),
          formatted: format(today.value, "do"),
        },
      };
    });

    // Week days according to the user's 'first day of the week' setting
    const weekDays = computed(() =>
      settings.isWeekStartOnSunday.value ? DAYS : [...DAYS.slice(1), DAYS[0]]
    );

    const monthDays = computed(() => {
      const daysInMonth = getDaysInMonth(today.value);
      return Array.from({ length: daysInMonth }, (_, i) => ({
        value: i + 1,
        label: format(
          new Date(today.value.getFullYear(), today.value.getMonth(), i + 1),
          "do"
        ),
      }));
    });

    const resetForm = () => {
      return {
        every: DEFAULT_EVERY,
        frequency: DEFAULT_FREQUENCY,
        startsType: DEFAULT_STARTS_TYPE,
        endsType: DEFAULT_ENDS_TYPE,
        days: [],
        dayOfMonth: currentDate.value.month.number,
        dateOfYear: {
          value: today.value,
          formatted: format(today.value, "MMMM do"),
        },
        startDate: {
          value: today.value,
          formatted: format(today.value, "MMMM do, yyyy"),
        },
        // Same day as start, so the Until calendar opens on the start month
        // where days before On are greyed out (a +1 year default opened a
        // year ahead, where those days are all valid and the constraint is
        // invisible).
        endDate: {
          value: today.value,
          formatted: format(today.value, "MMMM do, yyyy"),
        },
        recurrence: null,
      };
    };

    const form: Ref<Form> = ref(resetForm());

    const updateDateOfTheYear = (date: Date) => {
      // For a yearly recurrence, the date of the year is the
      // anniversary date on which to repeat but also acts as
      // the start date.
      // The date must therefore be in the future. If in the past,
      // add a year to make it a future date.
      let adjustedDate = date;
      if (date < today.value) {
        adjustedDate = addYears(adjustedDate, 1);
      }
      form.value.dateOfYear.value = adjustedDate;
      form.value.dateOfYear.formatted = format(adjustedDate, "MMMM do");
    };

    const startBoundary = () =>
      form.value.startsType === "from"
        ? form.value.startDate.value
        : today.value;

    const updateEndDate = (date: Date) => {
      const min = startBoundary();
      const clamped = isBefore(startOfDay(date), startOfDay(min)) ? min : date;
      form.value.endDate.value = clamped;
      form.value.endDate.formatted = format(clamped, "MMMM do, yyyy");
    };

    const updateStartDate = (date: Date) => {
      form.value.startDate.value = date;
      form.value.startDate.formatted = format(date, "MMMM do, yyyy");
      if (form.value.endsType === "until") {
        updateEndDate(form.value.endDate.value);
      }
    };

    const emitSelection = (recurrence: Recurrence) => {
      if (onCreationCallback.value) {
        onCreationCallback.value(recurrence);
      }

      closePicker();
    };

    const handleDateButtonClick = async (
      event: MouseEvent,
      dateType: "dateOfYear" | "startDate" | "endDate"
    ) => {
      // Save the position of the clicked element to open the pop-up next to it.
      const target = event.target;
      if (!(target instanceof HTMLElement)) return;
      datePickerTriggerEl.value = target;

      await showDatePicker(dateType);
    };

    const handleCreate = () => {
      const recurrence = buildRecurrenceFromForm();
      emitSelection(recurrence);
    };

    const selectDay = (day: string) => {
      const newDays = form.value.days.includes(day)
        ? form.value.days.filter((d) => d !== day)
        : [...form.value.days, day];

      form.value.days = newDays;
    };

    const showDatePicker = async (
      dateType: "dateOfYear" | "startDate" | "endDate"
    ) => {
      if (datePicker.value && datePickerTriggerEl.value) {
        try {
          const currentDate =
            dateType === "dateOfYear"
              ? form.value.dateOfYear.value
              : dateType === "startDate"
                ? form.value.startDate.value
                : form.value.endDate.value;
          const callback = (
            value:
              | { mode: "date"; date: Date }
              | { mode: "week"; week: { year: number; n: number } }
              | { mode: "month"; date: Date }
              | null
          ) => handleDateSelection(value, dateType);

          await datePicker.value.showPicker(
            datePickerTriggerEl.value,
            currentDate,
            callback,
            "left",
            {
              modes: ["date"],
              ...(dateType === "endDate" ? { minDate: startBoundary() } : {}),
            }
          );
        } catch (error) {
          window.console.error("Error calling showPicker:", error);
        }
      }
    };

    const handleDateSelection = (
      value:
        | { mode: "date"; date: Date }
        | { mode: "week"; week: { year: number; n: number } }
        | { mode: "month"; date: Date }
        | null,
      dateType: "dateOfYear" | "startDate" | "endDate"
    ) => {
      // The picker is opened with `modes: ["date"]` and no "Clear" button, so
      // only date selections occur; the checks keep the union types narrow.
      if (value?.mode === "date") {
        if (dateType === "dateOfYear") {
          updateDateOfTheYear(value.date);
        } else if (dateType === "startDate") {
          updateStartDate(value.date);
        } else {
          updateEndDate(value.date);
        }
      }
    };

    const buildRecurrenceFromForm = (): Recurrence => {
      const baseRule = {
        period: form.value.every,
        every: form.value.frequency,
      };

      let recurrence: Recurrence;

      if (form.value.frequency === "week" && form.value.days.length > 0) {
        const weekdays: Record<string, boolean> = {};
        form.value.days.forEach((day) => {
          weekdays[day] = true;
        });
        recurrence = { rule: { ...baseRule, weekdays } };
      } else if (form.value.frequency === "month") {
        recurrence = {
          rule: {
            every: "month",
            period: form.value.every,
            day: form.value.dayOfMonth,
          },
        };
      } else if (form.value.frequency === "year") {
        // For yearly recurrence, set start boundary to represent the anniversary date
        const month = monthFromDate(form.value.dateOfYear.value);
        const rule = {
          ...baseRule,
          day: form.value.dateOfYear.value.getDate(),
          month: month,
        };
        recurrence = { rule: rule };
      } else {
        recurrence = { rule: baseRule };
      }

      // Add start date if specified
      if (form.value.startsType === "from") {
        recurrence.start = form.value.startDate.value;
      }

      // Add end date if specified
      if (form.value.endsType === "until") {
        recurrence.end = form.value.endDate.value;
      }

      return recurrence;
    };

    const buildFormFromRecurrence = (recurrence: Recurrence) => {
      let form: Form = resetForm();
      if (recurrence.rule.every === "workday") {
        form.frequency = "week";
        form.days = ["monday", "tuesday", "wednesday", "thursday", "friday"];
        form.endsType = "forever";
      } else if (FREQUENCIES.includes(recurrence.rule.every)) {
        form.frequency = recurrence.rule.every;
        form.every = recurrence.rule.period ?? 1;
        if (recurrence.rule.every === "week") {
          if (recurrence.rule.weekdays) {
            form.days = Object.keys(recurrence.rule.weekdays).filter(
              (day) => day
            );
          }
        } else if (recurrence.rule.every === "month" && recurrence.rule.day) {
          form.every = recurrence.rule.period ?? 1;
          form.dayOfMonth = recurrence.rule.day;
        } else if (
          recurrence.rule.every === "year" &&
          recurrence.rule.month &&
          recurrence.rule.day
        ) {
          form.every = recurrence.rule.period ?? 1;
          const currentYear = today.value.getFullYear();
          const monthIdx = monthIndex(recurrence.rule.month);
          const day = recurrence.rule.day;
          form.dateOfYear.value = new Date(currentYear, monthIdx, day);
          if (form.dateOfYear.value < today.value) {
            form.dateOfYear.value = addYears(form.dateOfYear.value, 1);
          }
          form.dateOfYear.formatted = format(form.dateOfYear.value, "MMMM do");
        }
      }
      if ("start" in recurrence && recurrence.start) {
        form.startsType = "from";
        form.startDate.value = recurrence.start;
        form.startDate.formatted = format(recurrence.start, "MMMM do, yyyy");
      }
      if ("end" in recurrence && recurrence.end) {
        form.endsType = "until";
        const min =
          form.startsType === "from" ? form.startDate.value : today.value;
        const end = isBefore(startOfDay(recurrence.end), startOfDay(min))
          ? min
          : recurrence.end;
        form.endDate.value = end;
        form.endDate.formatted = format(end, "MMMM do, yyyy");
      }
      return form;
    };

    const updatePosition = async () => {
      if (!triggerEl.value) {
        computedStyle.value = {
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          zIndex: 1000,
        };
        return;
      }
      await nextTick();
      // If the trigger element is no longer in the DOM or has no geometry
      // (e.g., due to a synchronous flush/re-render), prefer a captured
      // anchor rect if available; otherwise keep the picker centered.
      let rect: DOMRect;
      if (!triggerEl.value || !document.body.contains(triggerEl.value)) {
        if (triggerRect.value) {
          rect = triggerRect.value;
        } else {
          return;
        }
      } else {
        rect = triggerEl.value.getBoundingClientRect();
      }
      if (
        (rect.width === 0 && rect.height === 0) ||
        (rect.right === 0 && rect.bottom === 0)
      ) {
        if (triggerRect.value) {
          rect = triggerRect.value;
        } else {
          return;
        }
      }
      const pickerHeight = pickerEl.value?.offsetHeight || 0;
      const pickerWidth = pickerEl.value?.offsetWidth || 0;
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;
      const spaceBelow = viewportHeight - rect.bottom;
      const spaceAbove = rect.top;
      const spaceRight = viewportWidth - rect.right;
      const spaceLeft = rect.left;
      const margin = 4; // Space between picker and trigger element

      // First try vertical positioning
      const canPlaceBelow = spaceBelow >= pickerHeight + margin;
      const canPlaceAbove = spaceAbove >= pickerHeight + margin;
      const canPlaceRight = spaceRight >= pickerWidth + margin;
      const canPlaceLeft = spaceLeft >= pickerWidth + margin;

      // If we can place it vertically, do so
      if (canPlaceBelow || canPlaceAbove) {
        computedStyle.value = {
          position: "fixed",
          ...(canPlaceBelow
            ? { top: `${rect.bottom + margin}px` }
            : { bottom: `${window.innerHeight - rect.top + margin}px` }),
          ...(currentPosition.value === "right"
            ? { right: `${window.innerWidth - rect.right}px` }
            : { left: `${rect.left}px` }),
          transform: "none",
          zIndex: 1000,
        };
        return;
      }

      // If we can't place it vertically, try horizontal positioning
      if (canPlaceRight || canPlaceLeft) {
        const verticalCenter = rect.top + rect.height / 2;
        const pickerTop = Math.max(margin, verticalCenter - pickerHeight / 2);
        const adjustedTop = Math.min(
          pickerTop,
          viewportHeight - pickerHeight - margin
        );

        computedStyle.value = {
          position: "fixed",
          top: `${adjustedTop}px`,
          ...(canPlaceRight
            ? { left: `${rect.right + margin}px` }
            : { right: `${window.innerWidth - rect.left + margin}px` }),
          transform: "none",
          zIndex: 1000,
        };
        return;
      }

      // If we can't place it anywhere with enough space, default to right side with scroll
      computedStyle.value = {
        position: "fixed",
        top: `${margin}px`,
        left: `${rect.right + margin}px`,
        maxHeight: `${viewportHeight - 2 * margin}px`,
        overflowY: "auto",
        transform: "none",
        zIndex: 1000,
      };
    };

    const handleClickOutside = (event: MouseEvent) => {
      // A bit hackish to prevents closing the RecurrencePicker when the DatePicker is closing
      //
      // Would be better to have a proper handling of bubbling events.
      if (isDisplayed.value && !datePicker.value?.isDisplayed) {
        const target = event.target;
        const isClickInsidePicker =
          target instanceof Node && pickerEl.value?.contains(target);
        const isClickInsideTrigger =
          target instanceof Node && triggerEl.value?.contains(target);

        if (!isClickInsidePicker && !isClickInsideTrigger) {
          event.stopPropagation();
          event.preventDefault();
          closePicker();
        }
      }
    };

    watch(
      () => form.value.startsType,
      () => {
        if (form.value.endsType === "until") {
          updateEndDate(form.value.endDate.value);
        }
      }
    );

    watch(
      () => isDisplayed.value,
      async (isVisible) => {
        if (isVisible) {
          await updatePosition();
          document.addEventListener("mousedown", handleClickOutside, {
            capture: true,
          });
        } else {
          document.removeEventListener("mousedown", handleClickOutside, {
            capture: true,
          });
        }
      }
    );

    const handleRecurrenceInput = async (keyboardEvent: KeyboardEvent) => {
      if (!parser.value) return;

      // Get current input value
      let currentValue = inputEl.value?.value || "";

      // For character keys, add the current key to the value for parsing
      if (
        keyboardEvent.key.length === 1 &&
        !keyboardEvent.ctrlKey &&
        !keyboardEvent.metaKey
      ) {
        currentValue += keyboardEvent.key;
      }

      if (!currentValue.trim()) return;

      const parsed = await controller.mandate.parse(
        parser.value,
        0,
        `every ${currentValue}`
      );

      const parsedCompletion = parsed?.completions?.completions[0];
      if (!parsedCompletion) return;
      const parsedValue = parsedCompletion.value.value;

      // Handle Recurrence results but ignore Date such as 'starting' and 'until' to
      // keep things simple.
      if (parsedCompletion.value.type === "recurrence") {
        form.value = buildFormFromRecurrence(parsedCompletion.value.value);
      } else {
        window.console.warn("Unknown recurrence", parsedValue);
      }
    };

    watch(
      () => isDisplayed.value,
      (isVisible) => {
        if (isVisible) {
          nextTick(() => {
            // Focus the popup div first to ensure keyboard events are captured
            pickerEl.value?.focus();
            // Then focus the input for immediate typing
            inputEl.value?.focus();
          });
        }
      }
    );

    const closePicker = () => {
      isDisplayed.value = false;
      isInputFocused.value = false;
      inputEl.value!.value = "";
    };

    const onTransitionEnd = () => {
      // Only reset styles when the picker is hidden
      if (!isDisplayed.value) {
        computedStyle.value = {};
      }
    };

    const triggerRect = ref<DOMRect | null>(null);

    const showPicker = async (
      element: HTMLElement | undefined,
      recurrence: Recurrence | null,
      callback: (selection: Recurrence) => void,
      position?: "left" | "right",
      anchorRect?: DOMRect
    ) => {
      triggerEl.value = element;
      triggerRect.value = anchorRect || null;
      onCreationCallback.value = callback;
      currentPosition.value = position || "left";
      parser.value = await makeParser(controller, "recurrence");

      // Set the form state to the passed recurrence
      form.value = resetForm();
      if (recurrence) {
        form.value = buildFormFromRecurrence(recurrence);
      }

      // Render hidden first, then compute the final anchored position and show
      computedStyle.value = {
        position: "fixed",
        zIndex: 1000,
        opacity: "0",
        pointerEvents: "none",
      };
      isDisplayed.value = true;

      await nextTick();
      await updatePosition();
      // Reveal after positioned
      computedStyle.value = {
        ...computedStyle.value,
        opacity: "1",
        pointerEvents: "auto",
      };

      isInputFocused.value = true;
    };

    return {
      inputEl,
      pickerEl,
      computedStyle,
      closePicker,
      showPicker,
      isDisplayed,
      triggerEl,
      handleRecurrenceInput,
      modalActions,
      // Form state
      form,
      weekDays,
      monthDays,
      currentDate,
      // Handlers
      handleDateButtonClick,
      handleCreate,
      selectDay,
      // DatePicker
      datePicker,
    };
  },
});
</script>

<style scoped lang="scss">
@include popup-transition();

.recurrence-picker {
  position: fixed; // Fallback position
  border-radius: $border-radius-l;
  padding: 4px 4px;
  width: 280px;
  transform-origin: top center;
  will-change: transform, opacity;
  @include themed() {
    background: t($bg-tooltip);
    border: 1px solid t($border);
    box-shadow: $tooltip;
    color: t($text);
  }
}

.tabs {
  display: flex;
  margin-bottom: 12px;
  @include themed() {
    border-bottom: 1px solid t($border);
  }

  .tab-button {
    flex: 1;
    padding: 8px 12px;
    background: none;
    border: none;
    border-bottom: 1px solid transparent;
    @extend %font-body-regular;
    text-align: center;
    margin-bottom: -1px;
    @include transition(color 100ms ease);
    @include themed() {
      color: t($grey-50);
    }

    &:hover {
      @include themed() {
        color: t($grey-70);
      }
    }

    &.active {
      font-weight: 500;
      @include themed() {
        color: t($primary);
        border-bottom-color: t($grey-50);
      }
    }
  }
}

.recurrence-input {
  padding: 4px;

  .natural-date-input {
    width: calc(100% - 24px);
    padding: 8px 12px;
    border-radius: $border-radius-m;
    @extend %font-caption-regular;
    @include themed() {
      background: t($bg);
      border: 1px solid t($grey-15);
      color: t($grey-80);
      &::placeholder {
        color: t($grey-50);
        @extend %font-caption-medium;
      }
      &:focus {
        background: t($grey-10);
        border: 1px solid t($grey-10);
      }
    }
  }
}

.actions {
  padding-block: 8px;
  padding-inline-end: 8px;
  padding-inline-start: 4px;
  display: flex;
  gap: 8px;
  justify-content: space-between;
}

// Form sections
.every-section,
.on-section,
.starts-section,
.ends-section {
  padding: 12px 8px;
}

.section-title {
  @extend %font-caption-medium;
  margin-bottom: 8px;
  @include themed() {
    color: t($grey-50);
  }
}

.every-controls {
  display: flex;
  gap: 8px;
  align-items: center;
}

.frequency-select {
  flex: 1;
  padding: 6px 8px;
  padding-inline-end: 24px;
  border-radius: $border-radius-m;
  @extend %font-caption-regular;
  @include themed() {
    background-color: t($bg);
    border: 1px solid t($grey-15);
    color: t($grey-80);
    &:focus {
      background-color: t($grey-10);
      border: 1px solid t($grey-10);
    }
  }
}

.number-select,
.day-select {
  padding: 6px 8px;
  padding-inline-end: 24px;
  border-radius: $border-radius-m;
  @extend %font-caption-regular;
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e");
  background-position: right 6px center;
  background-repeat: no-repeat;
  background-size: 16px;
  appearance: none;
  @include themed() {
    background-color: t($bg);
    border: 1px solid t($grey-15);
    color: t($grey-80);
    &:focus {
      background-color: t($grey-10);
      border: 1px solid t($grey-10);
    }
  }
}

.number-select {
  width: 60px;
}

.frequency-select {
  flex: 1;
  padding: 6px 8px;
  padding-inline-end: 24px;
  border-radius: $border-radius-m;
  @extend %font-caption-regular;
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e");
  background-position: right 6px center;
  background-repeat: no-repeat;
  background-size: 16px;
  appearance: none;
  @include themed() {
    background-color: t($bg);
    border: 1px solid t($grey-15);
    color: t($grey-80);
    &:focus {
      background-color: t($grey-10);
      border: 1px solid t($grey-10);
    }
  }
}

.days-checkboxes {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 2px;
}

.day-checkbox {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px 4px;
  border-radius: $border-radius-m;
  @include transition(background-color 100ms ease, border-color 100ms ease);
  @include themed() {
    &:hover {
      background: t($bg-menu-hover);
    }
    &.selected {
      background: t($grey-15);
      .checkbox-label {
        color: t($primary);
        font-weight: 500;
      }
    }
  }
}

.checkbox-input {
  margin: 0;
  margin-inline-end: 4px;
  @include themed() {
    accent-color: t($primary);
  }
}

.checkbox-label {
  @extend %font-caption-regular;
  @include themed() {
    color: t($grey-60);
  }
}

.day-button {
  padding: 8px 4px;
  border-radius: $border-radius-m;
  @extend %font-caption-regular;
  @include transition(background-color 100ms ease, border-color 100ms ease);
  @include themed() {
    background: t($bg);
    color: t($grey-60);
    &:hover {
      background: t($bg-menu-hover);
    }
    &.selected {
      background: t($grey-15);
      color: t($primary);
      font-weight: 500;
    }
  }
}

.date-button {
  padding: 8px 12px;
  border-radius: $border-radius-m;
  @extend %font-caption-regular;
  @include transition(background-color 100ms ease);
  @include themed() {
    background: t($bg);
    border: 1px solid t($grey-15);
    color: t($grey-80);
    &:hover {
      background: t($bg-menu-hover);
    }
  }
}

.starts-options,
.ends-options {
  display: flex;
  gap: 16px;
  margin-bottom: 8px;
  padding: 0 2px;
}

.radio-option {
  display: flex;
  align-items: center;
  @include transition(color 100ms ease);
  @include themed() {
    color: t($grey-50);
    &:hover {
      color: t($grey-70);
    }
    &:has(.radio-input:checked) {
      color: t($grey-70);
    }
  }
}

.radio-input {
  margin: 0;
  margin-inline-end: 6px;
  @include themed() {
    accent-color: t($primary);
  }
}

.radio-label {
  @extend %font-caption-regular;
}
</style>
